import { HStack } from '@cbhq/cds-web/layout';
import { TextLabel1 } from '@cbhq/cds-web/typography';
import { Icon } from '@cbhq/cds-web/icons';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { memo, useMemo } from 'react';
import { useUSDCRewards, USDCRewardsEligibility } from '@/hooks/useUSDCRewards';
import { messages } from '@/messages';

type USDCRewardsChipProps = {
  /** Whether to show the tooltip on hover. Defaults to true. */
  showTooltip?: boolean;
};

/**
 * USDCRewardsChip - A simple chip component for displaying USDC rewards eligibility
 *
 * This component shows a chip with a primary color wash background, rewards icon,
 * and USDC text when the user is eligible for USDC rewards (retail or institutional).
 *
 * The chip content varies based on user eligibility:
 * - Retail users see "2% USDC"
 * - Institutional users see "USDC"
 *
 * Each variant includes a tooltip with detailed information about the rewards.
 *
 * @returns JSX element or null if user is not eligible for USDC rewards
 */
export const USDCRewardsChip = memo(function USDCRewardsChip({
  showTooltip = true,
}: USDCRewardsChipProps) {
  const usdcRewardsStatus = useUSDCRewards();

  const chip = useMemo(() => {
    // Don't render if user is not eligible for USDC rewards
    if (usdcRewardsStatus === USDCRewardsEligibility.NA) {
      return null;
    }

    const isRetail = usdcRewardsStatus === USDCRewardsEligibility.RETAIL_USDC_REWARDS;
    const tooltipKey = isRetail
      ? 'usdcRewards.retail.tooltip'
      : 'usdcRewards.institutional.tooltip';

    const chipText = isRetail ? '2% USDC' : 'USDC';

    const chipContent = (
      <HStack
        borderRadius={100}
        paddingX={1}
        paddingY={0.5}
        background="bgPrimaryWash"
        alignItems="center"
        gap={0.5}
      >
        <Icon name="learningRewardsProduct" size="s" color="fgPrimary" />
        <TextLabel1 noWrap color="fgPrimary" as="span">
          {chipText}
        </TextLabel1>
      </HStack>
    );

    if (!showTooltip) {
      return chipContent;
    }

    return <Tooltip content={messages[tooltipKey]}>{chipContent}</Tooltip>;
  }, [usdcRewardsStatus, showTooltip]);

  return chip;
});
